import type { SupplierDocumentCategory } from '@tandemone/types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { extractErrorMessage } from '@/api/extract-error'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import {
  type UploadedDocument,
  FileDropZone,
  getDocument,
  UploadedFileRow
} from '@/domains/document'
import { captureException } from '@/domains/observability'
import { navigateTab, openBlankTab } from '@/lib/new-tab'

import {
  createSupplierDocument,
  deleteSupplierDocument,
  getSupplierDocuments
} from './api'
import { invalidateOnboarding } from './invalidate-onboarding'

const DOC_MAX_BYTES = 200 * 1024 * 1024
const ACCEPT = 'application/pdf,image/png,image/jpeg,video/mp4'

const CATEGORY_ORDER: ReadonlyArray<SupplierDocumentCategory> = [
  'facility',
  'equipment',
  'product',
  'management_team',
  'other'
]

/**
 * 'logo' is a separate upload on the general-info form, not a picker choice
 * here, but the type requires it as a key.
 */
const CATEGORY_LABELS: Record<SupplierDocumentCategory, string> = {
  facility: 'Facility photos and videos',
  equipment: 'Equipment and production machines',
  product: 'Products',
  management_team: 'Management team',
  other: 'Other',
  logo: 'Logo'
}

const CATEGORY_HINTS: Record<SupplierDocumentCategory, string> = {
  facility: 'Exterior, interior, or floor shots of your facility.',
  equipment: 'Photos of machines and production equipment.',
  product: 'Examples of products you make.',
  management_team: 'Headshots or team photos.',
  other: 'Anything else buyers should see.',
  logo: 'Company logo image.'
}

export function SupplierDocumentsForm() {
  const queryClient = useQueryClient()
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [pendingRemoval, setPendingRemoval] = useState<{
    id: string
    fileName: string
  } | null>(null)

  const { data } = useQuery({
    queryKey: ['supplier-documents'],
    queryFn: ({ signal }) => getSupplierDocuments(signal)
  })

  const createMutation = useMutation({
    mutationFn: createSupplierDocument,
    onSuccess: async () => {
      await invalidateOnboarding(queryClient, 'supplier-documents')
    },
    onError: error => {
      captureException(error)
      setSubmitError(extractErrorMessage(error))
    }
  })

  const deleteMutation = useMutation({
    mutationFn: deleteSupplierDocument,
    onSuccess: async () => {
      await invalidateOnboarding(queryClient, 'supplier-documents')
      setPendingRemoval(null)
    },
    onError: error => {
      captureException(error)
      setSubmitError(extractErrorMessage(error))
    }
  })

  const docs = data?.documents ?? []

  function docsForCategory(category: SupplierDocumentCategory) {
    return docs.filter(doc => doc.category === category)
  }

  function handleUploaded(
    category: SupplierDocumentCategory,
    uploaded: UploadedDocument
  ) {
    setSubmitError(null)
    createMutation.mutate({ documentId: uploaded.documentId, category })
  }

  async function viewDocument(documentId: string, tab: Window | null) {
    try {
      const result = await getDocument(documentId)
      navigateTab(result.downloadUrl, tab)
    } catch (error) {
      tab?.close()
      if (error instanceof Error) captureException(error)
      setSubmitError(extractErrorMessage(error))
    }
  }

  function confirmRemoval() {
    if (!pendingRemoval) return
    deleteMutation.mutate(pendingRemoval.id)
  }

  return (
    <div className='flex w-full flex-col gap-4'>
      {CATEGORY_ORDER.map(category => {
        const items = docsForCategory(category)
        return (
          <section
            key={category}
            className='flex flex-col gap-3 rounded-2xl border border-border bg-page p-4 sm:p-6'
          >
            <div>
              <p className='text-[14px] font-semibold text-ink'>
                {CATEGORY_LABELS[category]}
              </p>
              <p className='mt-0.5 text-[12px] text-ink-soft'>
                {CATEGORY_HINTS[category]}
              </p>
            </div>

            {items.length > 0 && (
              <ul className='flex flex-col gap-2'>
                {items.map(item => (
                  <li key={item.id}>
                    <UploadedFileRow
                      fileName={item.fileName}
                      onView={() => void viewDocument(item.id, openBlankTab())}
                      onRemove={() =>
                        setPendingRemoval({
                          id: item.id,
                          fileName: item.fileName
                        })
                      }
                    />
                  </li>
                ))}
              </ul>
            )}

            <FileDropZone
              accept={ACCEPT}
              maxBytes={DOC_MAX_BYTES}
              value={null}
              onUploaded={uploaded => handleUploaded(category, uploaded)}
            />
          </section>
        )
      })}

      {submitError && <p className='text-sm text-red-600'>{submitError}</p>}

      <Dialog
        open={pendingRemoval !== null}
        onOpenChange={open => {
          if (!open && !deleteMutation.isPending) setPendingRemoval(null)
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove this file?</DialogTitle>
            <DialogDescription>
              {/* the dialog is a grid, so an unbroken filename widens it past
                  the viewport */}
              <span className='break-all'>{pendingRemoval?.fileName}</span> will
              be removed from your documents.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <button
              type='button'
              onClick={() => setPendingRemoval(null)}
              disabled={deleteMutation.isPending}
              className='min-h-11 w-full cursor-pointer rounded-md border border-border bg-page px-3 text-[12px] font-medium text-ink-soft hover:bg-cream/40 hover:text-ink disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0 sm:w-auto sm:py-1.5'
            >
              Cancel
            </button>
            <button
              type='button'
              onClick={confirmRemoval}
              disabled={deleteMutation.isPending}
              className='min-h-11 w-full cursor-pointer rounded-md bg-destructive px-3 text-[12px] font-medium text-white transition-colors hover:bg-[#7f1d1d] disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0 sm:w-auto sm:py-1.5'
            >
              {deleteMutation.isPending ? 'Removing…' : 'Remove'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
